import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { LogIn, Mail, Lock, KeyRound, Loader2, ArrowRight, Sun, Moon } from 'lucide-react';
import api from '../api/axios';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();                          // ← ADD
  const pendingPlan = searchParams.get('plan'); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [isMfaStep, setIsMfaStep] = useState(false); // 🔥 New State
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [isDark, setIsDark] = useState(() => localStorage.getItem('theme') !== 'light');
  useEffect(() => {
    if (isDark) { document.documentElement.classList.add('dark'); localStorage.setItem('theme', 'dark'); } 
    else { document.documentElement.classList.remove('dark'); localStorage.setItem('theme', 'light'); }
  }, [isDark]);

  const handleLoginSuccess = () => {                                 // ← ADD
    localStorage.setItem('isAuthenticated', 'true');
    const dest = pendingPlan ? `/dashboard?checkout=${pendingPlan}` : '/dashboard';
    navigate(dest);
  };

  // Step 1: Verify Password
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);
      const res = await api.post('/token', formData);
      if (res.data.message === "MFA_REQUIRED") {
        setIsMfaStep(true);
      } else {
        handleLoginSuccess();                                        // ← CHANGE
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await api.post('/verify-mfa', { email, otp });
      handleLoginSuccess();                                          // ← CHANGE
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50 dark:bg-[#05050A] text-slate-900 dark:text-slate-200 font-sans flex items-center justify-center relative overflow-hidden overflow-x-hidden transition-colors duration-500 px-4 py-4 sm:py-6">
      <style>{customStyles}</style>
      
      {/* ... keep your theme toggle and ambient glows ... */}

      <div className="w-full max-w-md relative z-10 animate-in fade-in zoom-in-95 duration-500">
        
        <div className="flex flex-col items-center justify-center mb-6 sm:mb-8">
          <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-[1rem] sm:rounded-[1.2rem] flex items-center justify-center mb-4 shadow-md dark:shadow-2xl backdrop-blur-md">
            {isMfaStep ? <KeyRound size={24} className="text-indigo-600 dark:text-indigo-400" strokeWidth={1.5}/> : <LogIn size={24} className="text-indigo-600 dark:text-indigo-400 ml-1" strokeWidth={1.5} />}
          </div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-wide text-slate-900 dark:text-white text-center transition-colors">
            {isMfaStep ? 'Verification' : 'RecruitGuru'}
          </h1>
          <p className="text-slate-500 text-[12px] font-normal tracking-[0.1em] [word-spacing:-0.05em] mt-2 text-center">
            {isMfaStep ? `Enter the code sent to ${email}` : 'Sign in to your dashboard'}
          </p>
        </div>

        <form onSubmit={isMfaStep ? handleMfaSubmit : handlePasswordSubmit} className="bg-white/80 dark:bg-[#0a0a0a]/50 backdrop-blur-xl border border-slate-200 dark:border-white/10 p-6 sm:p-8 rounded-[2rem] shadow-xl dark:shadow-2xl flex flex-col gap-5">
          
          {!isMfaStep ? (
             <div className="space-y-4 animate-in slide-in-from-left-4 fade-in">
               <div>
                 <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-1.5 flex items-center gap-2">
                   <Mail size={14} className="text-indigo-500 dark:text-indigo-400"/> Email Address
                 </label>
                 <input 
                   type="email" required disabled={isLoading}
                   placeholder="you@company.com" value={email} onChange={e => setEmail(e.target.value)}
                   className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
                 />
               </div>
               <div>
                 <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-1.5 flex items-center gap-2">
                   <Lock size={14} className="text-indigo-500 dark:text-indigo-400"/> Password
                 </label>
                 <input 
                   type="password" required disabled={isLoading}
                   placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)}
                   className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-sm placeholder:text-[12px] font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
                 />
               </div>
             </div>
          ) : (
             <div className="space-y-4 animate-in slide-in-from-right-4 fade-in">
               <div>
                 <label className="block text-[12px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mb-1.5 flex items-center gap-2">
                   <KeyRound size={14} className="text-indigo-500 dark:text-indigo-400"/> 6-Digit Code
                 </label>
                 <input 
                   type="text" required disabled={isLoading} maxLength={6}
                   placeholder="123456" value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                   className="w-full bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3 text-center tracking-[0.5em] text-lg font-mono text-slate-900 dark:text-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all disabled:opacity-50"
                 />
               </div>
             </div>
          )}

          {error && (
            <div className="text-rose-600 dark:text-rose-500 text-xs font-medium bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/20 p-2.5 rounded-xl">
              {error}
            </div>
          )}

          <button 
            type="submit" disabled={isLoading || (!isMfaStep && (!email || !password)) || (isMfaStep && otp.length !== 6)}
            className="w-full bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-500 text-white tracking-[0.1em] [word-spacing:-0.05em] rounded-xl py-3.5 text-xs font-bold tracking-wide transition-all shadow-md dark:shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 mt-1"
          >
            {isLoading ? <Loader2 size={16} className="animate-spin"/> : isMfaStep ? 'Verify & Enter' : 'Sign In Securely'}
            {!isLoading && <ArrowRight size={16} strokeWidth={2}/>}
          </button>
        </form>

        <div className="mt-6 text-center space-y-2">
          {/* 🔥 New Forgot Password Link */}
          {!isMfaStep && (
            <p className="text-slate-500 dark:text-slate-400 text-[12px] font-medium tracking-[0.1em] [word-spacing:-0.05em]">
              Forgot your password?{' '}
              <Link to="/forgot-password" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-white transition-colors ml-1">
                Reset it
              </Link>
            </p>
          )}
          <p className="text-slate-500 dark:text-slate-400 text-[12px] font-medium tracking-[0.1em] [word-spacing:-0.05em]">
            Don't have an account?{' '}
            <Link to="/register" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-white transition-colors ml-1">
              Create one
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
};

export default Login;